const finite = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;

function rectEdges(rect) {
  const left = finite(rect?.left);
  const top = finite(rect?.top);
  const width = Math.max(0, finite(rect?.width));
  const height = Math.max(0, finite(rect?.height));
  return {
    left,
    top,
    width,
    height,
    right: finite(rect?.right, left + width),
    bottom: finite(rect?.bottom, top + height),
  };
}

function fitsViewport(candidate, width, height, viewport, padding) {
  return candidate.left >= padding
    && candidate.top >= padding
    && candidate.left + width <= viewport.width - padding
    && candidate.top + height <= viewport.height - padding;
}

function fitsPanel(candidate, width, height, panel, padding) {
  if (!panel) return true;
  return candidate.top >= panel.top + padding
    && candidate.top + height <= panel.bottom - padding;
}

/**
 * Pick a fixed-position tooltip location around an inventory slot.
 * Prefer staying within the inventory panel, and never place a tooltip below
 * the panel just because there is still room in the viewport.
 */
export function inventoryTooltipPosition(slotRect, tipSize, viewportSize, panelRect = null, gap = 8, padding = 10) {
  const slot = rectEdges(slotRect);
  const tip = {
    width: Math.max(0, finite(tipSize?.width)),
    height: Math.max(0, finite(tipSize?.height)),
  };
  const viewport = {
    width: Math.max(0, finite(viewportSize?.width)),
    height: Math.max(0, finite(viewportSize?.height)),
  };
  const panel = panelRect ? rectEdges(panelRect) : null;
  const centeredLeft = slot.left + slot.width / 2 - tip.width / 2;
  const centeredTop = slot.top + slot.height / 2 - tip.height / 2;
  const candidates = [
    { name: 'below', left: centeredLeft, top: slot.bottom + gap },
    { name: 'above', left: centeredLeft, top: slot.top - tip.height - gap },
    { name: 'right', left: slot.right + gap, top: centeredTop },
    { name: 'left', left: slot.left - tip.width - gap, top: centeredTop },
  ];
  const clamp = candidate => ({
    ...candidate,
    left: Math.round(Math.max(padding, Math.min(candidate.left, viewport.width - tip.width - padding))),
    top: Math.round(Math.max(padding, Math.min(candidate.top, viewport.height - tip.height - padding))),
  });
  const visible = candidates.map(clamp).filter(candidate => fitsViewport(candidate, tip.width, tip.height, viewport, padding));
  const insidePanel = visible.filter(candidate => fitsPanel(candidate, tip.width, tip.height, panel, padding));
  const preferred = insidePanel.find(candidate => candidate.name === 'below')
    || insidePanel.find(candidate => candidate.name === 'above')
    || insidePanel.find(candidate => candidate.name === 'right')
    || insidePanel.find(candidate => candidate.name === 'left')
    || visible.find(candidate => candidate.name === 'above')
    || visible[0]
    || clamp(candidates[0]);
  return { left: preferred.left, top: preferred.top };
}
